/* eslint-disable @nfq/component-single-hook */
import type {ReactNode} from 'react';

import {observer} from 'mobx-react-lite';

import {useLinkTracking} from 'UI/hooks/useLinkTracking';
import {useTrackingPageView} from 'UI/hooks/useTracking';

/**
 * The `TrackingWrapper` component is responsible for handling tracking functionality such as
 * page views and link interactions. It wraps its children with tracking behavior, ensuring
 * that page views and link clicks are tracked. The component does not alter the visual rendering
 * of its children and simply returns them as-is.
 *
 * @param props          The component props.
 * @param props.children The child components that will be rendered inside the `TrackingWrapper`.
 * @returns The `TrackingWrapper` component that tracks page views and link clicks while rendering its children.
 *
 * @example
 * ```tsx
 * <TrackingWrapper>
 *   <YourComponent />
 * </TrackingWrapper>
 * ```
 */
const TrackingWrapper = observer(({children}: {children: ReactNode}) => {
    useTrackingPageView();
    useLinkTracking();

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
});

TrackingWrapper.displayName = 'TrackingWrapper';

export {TrackingWrapper};